/**
 * <s-storybook> — a flip-through book. Each direct child of [data-book-pages] with
 * [data-book-page] is one page. Pages are stacked; turning forward rotates the current page
 * away to the left on its spine to reveal the next one, turning back swings it home.
 *
 * Controls: clicking a page (left 30% goes back, the rest goes forward), buttons with
 * commandfor="<id>" command="--next" / "--prev", Left/Right arrow keys
 * while the book has focus, and horizontal swipes. Hidden pages are inert so only the open page
 * is reachable by keyboard and assistive tech. Motion is CSS-driven and disabled in CSS for
 * prefers-reduced-motion.
 */
class SStorybook extends HTMLElement {
  get pages() {
    return [...this.querySelectorAll('[data-book-page]')];
  }

  connectedCallback() {

    this.status = this.querySelector('[data-book-status]');
    this.prevButton = this.querySelector('[command="--prev"]');
    this.nextButton = this.querySelector('[command="--next"]');
    this.index = 0;



    this.onCommand = (event) => {
      if (event.command === '--next') this.go(this.index + 1);
      if (event.command === '--prev') this.go(this.index - 1);
    };
    this.onKey = (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1); }
    };
    this.onPointerDown = (event) => { this.startX = event.clientX; this.startY = event.clientY; };
    this.onPointerUp = (event) => {
      if (this.startX == null) return;
      const dx = event.clientX - this.startX;
      const dy = event.clientY - this.startY;
      this.startX = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) { this.go(this.index + (dx < 0 ? 1 : -1)); return; }
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) {
        if (event.target.closest('a, button, input, select, textarea')) return;
        if (String(window.getSelection?.() || '').length) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const onLeft = event.clientX - rect.left < rect.width * 0.3;
        this.go(this.index + (onLeft ? -1 : 1));
      }
    };

    this.addEventListener('command', this.onCommand);
    this.addEventListener('keydown', this.onKey);
    const stage = this.querySelector('[data-book-pages]');
    stage?.addEventListener('pointerdown', this.onPointerDown);
    stage?.addEventListener('pointerup', this.onPointerUp);
    this.observer = new MutationObserver(() => this.render());
    if (stage) this.observer.observe(stage, { childList: true, subtree: true });
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.onCommand);
    this.removeEventListener('keydown', this.onKey);
    this.observer?.disconnect();
  }

  go(next) {
    const clamped = Math.max(0, Math.min(this.pages.length - 1, next));
    if (clamped === this.index) return;
    this.index = clamped;
    this.render();
  }

  render() {
    const pages = this.pages;
    if (!pages.length) return;
    if (this.index > pages.length - 1) this.index = pages.length - 1;
    pages.forEach((page, i) => {
      page.style.zIndex = String(pages.length - i);
      page.toggleAttribute('data-turned', i < this.index);
      const open = i === this.index;
      page.toggleAttribute('data-open', open);
      page.inert = !open;
      page.setAttribute('aria-hidden', open ? 'false' : 'true');
    });
    if (this.prevButton) this.prevButton.disabled = this.index === 0;
    if (this.nextButton) this.nextButton.disabled = this.index === pages.length - 1;
    if (this.status) {
      const template = this.status.dataset.template || '[current] / [total]';
      this.status.textContent = template
        .replace('[current]', String(this.index + 1))
        .replace('[total]', String(pages.length));
    }
  }
}

if (!customElements.get('s-storybook')) customElements.define('s-storybook', SStorybook);
